// Aim assist (Phase C, amoeba-input plan): the one system serving keyboard-
// only aim now and gamepad stick+soft-lock later. Mouse users get raw cursor
// aim, zero assist — this system only takes over when the mouse goes quiet.
//
// Heading source: facing follows the last nonzero movement input (persisted
// per-player in lastHeading — a WeakMap, same pattern as hazards.ts's
// per-entity cooldown map, since this isn't state a save needs to carry).
// A facing-cone search then finds a soft-lock target for fine aim; the
// committed dir turns toward the chosen bearing at a capped rate so it never
// snaps, matching the mouse-free ARPG convention (Hades/Halls of Torment).

import type { Entity, Vec2 } from '../components'
import type { GameWorld } from '../world'
import type { GameInput } from '../controls'
import { T } from '../tunables'

type Targetable = Entity & { position: Vec2 }

function normalize(v: Vec2): Vec2 {
  const len = Math.hypot(v.x, v.y)
  return len > 1e-6 ? { x: v.x / len, y: v.y / len } : { x: 0, y: 0 }
}

/** Rotate `from` toward `to` by at most `maxRad`. Always returns a unit vector. */
function rotateToward(from: Vec2, to: Vec2, maxRad: number): Vec2 {
  const fromAngle = Math.atan2(from.y, from.x)
  const toAngle = Math.atan2(to.y, to.x)
  const diff = Math.atan2(Math.sin(toAngle - fromAngle), Math.cos(toAngle - fromAngle))
  const clamped = Math.max(-maxRad, Math.min(maxRad, diff))
  const angle = fromAngle + clamped
  return { x: Math.cos(angle), y: Math.sin(angle) }
}

/** Last nonzero movement heading per player, held while the move vector goes
 *  to zero (so releasing WASD keeps facing where you were walking). */
const lastHeading = new WeakMap<Entity, Vec2>()

/** Pure cone search — no world access. Candidates within `maxRange` of
 *  `origin` and within `coneDeg`/2 of `facing` score on angle offset +
 *  distance (equal weight, both normalized 0..1; lower wins). The previous
 *  target keeps a scoring edge (T.aim.stickyBonus) so a marginally-better
 *  challenger can't cause flicker. */
export function coneTarget(
  candidates: Iterable<Targetable>,
  origin: Vec2,
  facing: Vec2,
  coneDeg: number,
  maxRange: number,
  prev?: Entity,
): Entity | undefined {
  const halfCone = (coneDeg / 2) * (Math.PI / 180)
  const facingAngle = Math.atan2(facing.y, facing.x)
  let best: Entity | undefined
  let bestScore = Infinity
  for (const c of candidates) {
    const dx = c.position.x - origin.x
    const dy = c.position.y - origin.y
    const dist = Math.hypot(dx, dy)
    if (dist > maxRange) continue
    const bearing = Math.atan2(dy, dx)
    const diff = Math.atan2(Math.sin(bearing - facingAngle), Math.cos(bearing - facingAngle))
    const absDiff = Math.abs(diff)
    if (absDiff > halfCone) continue
    let score = absDiff / halfCone + dist / maxRange
    if (c === prev) score -= T.aim.stickyBonus
    if (score < bestScore) {
      bestScore = score
      best = c
    }
  }
  return best
}

export function aimSystem(world: GameWorld, player: Entity, input: GameInput, dt: number, nowMs: number): void {
  const prevDir = player.aim?.dir ?? { x: 1, y: 0 }
  const cursorActive = nowMs - input.mouseActivityAt < T.aim.cursorHoldSec * 1000

  if (cursorActive) {
    const dx = input.mouse.x - player.position.x
    const dy = input.mouse.y - player.position.y
    const dist = Math.hypot(dx, dy)
    // Cursor on top of you: no direction — hold the last one, no assist.
    const dir = dist < 0.05 ? prevDir : { x: dx / dist, y: dy / dist }
    player.aim = { dir, point: { x: input.mouse.x, y: input.mouse.y }, source: 'cursor' }
    return
  }

  // Heading source: 8-way movement facing, held across zero-move frames.
  const move = input.actions.vec('move')
  let facing = lastHeading.get(player) ?? prevDir
  if (move.x !== 0 || move.y !== 0) {
    facing = normalize(move)
    lastHeading.set(player, facing)
  }

  const candidates: Targetable[] = []
  for (const pred of world.with('predator', 'position', 'renderable')) {
    if (pred.absorbing || pred.chainSegment) continue
    candidates.push(pred)
  }
  for (const box of world.with('container', 'position', 'renderable')) candidates.push(box)

  const target = coneTarget(candidates, player.position, facing, T.aim.coneDeg, T.aim.maxRange, player.aim?.target)
  const desired = target ? normalize({ x: target.position.x - player.position.x, y: target.position.y - player.position.y }) : facing

  const maxRad = (T.aim.turnRateDeg * Math.PI / 180) * dt
  const dir = rotateToward(prevDir, desired, maxRad)

  player.aim = {
    dir,
    point: { x: player.position.x + dir.x * T.aim.maxRange, y: player.position.y + dir.y * T.aim.maxRange },
    source: 'heading',
    target,
  }
}
